import React, { useRef, useState } from 'react'
import clsx from 'clsx'
import { useBreakpoints } from '@obeta/data/lib/hooks'
import { MagnifyImage } from '../magnify-image/MagnifyImage'
import type { ContentItem } from '@obeta/data/lib/hooks/useArticleGalleryContent'
import { Image } from '../image/Image'
import { GalleryModal } from '../gallery-modal/GalleryModal'
import { useTranslation } from 'react-i18next'
import { renderLabel, getItemUrl, checkIs3DImage, checkIsVideo } from './helpers'
import { GalleryStripSection } from './GalleryStripSection'
import styles from './InlineMediaBox.module.scss'

type InlineMediaBoxProps = {
  items: ContentItem[]
}

export const InlineMediaBox: React.FC<InlineMediaBoxProps> = ({ items }) => {
  const { desktop } = useBreakpoints()
  const [modalOpen, setModalOpen] = useState(false)
  const [selectedItemIndex, updateSelectedItemIndex] = useState(0)
  const [magnifiedItemIndex, updateMagnifiedItemIndex] = useState(-1)
  const isTouching = useRef(false)
  const { t } = useTranslation()

  if (!items || items.length === 0) return null

  const getImageContainerClassName = (): string => {
    if (!desktop) return styles.singleImageContainer

    const imageContainerMap: Record<number, string> = {
      1: styles.singleImageContainer,
      2: styles.twoImageContainer,
      3: styles.threeImageContainer,
      4: styles.fourImageContainer,
    }

    return imageContainerMap[items.length] ?? styles.moreThenFourImageContainer
  }

  const getStripeItems = () => {
    if (!items.length) return { mainItems: [], stripItems: [], overflowItems: [] }

    const mainCount = desktop ? (items.length > 4 ? 3 : items.length) : 1

    let stripCount: number

    if (desktop) {
      // When we have more than 7 items in total ( desktop ), we show a placeholder instead of the fourth strip item, so we only need 3
      stripCount = items.length > 7 ? 3 : 4
    } else {
      stripCount = items.length > 5 ? 3 : 4
    }

    const mainItems = items.slice(0, mainCount)
    const stripItems = items.slice(mainCount, mainCount + stripCount)
    const overflowItems = items.slice(mainCount + stripCount)

    return {
      mainItems,
      stripItems,
      overflowItems,
    }
  }

  const { mainItems, stripItems, overflowItems } = getStripeItems()

  const isShowMorePlaceholderVisible = desktop ? items.length > 7 : items.length > 5

  const itemsLeft = overflowItems.length

  const onSelectItemHandler = (index: number) => {
    setModalOpen(true)
    updateSelectedItemIndex(index)
  }

  const onMagnifyImage = (index: number) => {
    // Skip magnification on touch events to avoid conflicts with tap-to-select
    if (isTouching.current) return
    updateMagnifiedItemIndex(index)
  }

  return (
    <>
      <div
        className={styles.wrapper}
        onTouchStart={() => {
          isTouching.current = true
        }}
        onTouchEnd={(e) => {
          // React creates synthetic mouse events after touch events complete
          // We need these synthetic events for click handling, but want to prevent them
          // from triggering mousemove/hover behaviors
          // Using a timeout ensures we block these synthetic events while allowing real mouse events later
          setTimeout(() => {
            isTouching.current = false
          }, 500)
        }}
      >
        {magnifiedItemIndex !== -1 && (
          <div
            className={styles.magnifyImageContainer}
            onMouseLeave={() => onMagnifyImage(-1)}
            onClick={() => onSelectItemHandler(magnifiedItemIndex)}
          >
            <MagnifyImage
              src={getItemUrl(items[magnifiedItemIndex])}
              alt={items[magnifiedItemIndex].description}
              className={styles.MagnifyImage}
              zoomScale={1.8}
            />
          </div>
        )}
        <div className={clsx(getImageContainerClassName())}>
          {mainItems.map((mainItem) => {
            const contentTypeLabel = renderLabel(mainItem, t, mainItems.length === 1)

            const noZoom = checkIs3DImage(mainItem) || checkIsVideo(mainItem)

            return (
              <div
                onMouseMove={() => onMagnifyImage(mainItem.itemIndex)}
                key={`main-${mainItem.itemIndex}`}
                className={clsx(styles.mainImageWrapper, {
                  [styles.noZoom]: noZoom,
                  [styles.activeMagnified]: magnifiedItemIndex === mainItem.itemIndex,
                })}
                role="button"
                tabIndex={0}
                onClick={() => onSelectItemHandler(mainItem.itemIndex)}
              >
                <Image
                  variant="product"
                  src={getItemUrl(mainItem)}
                  alt={mainItem.description}
                  className={clsx(styles.stripeImage, contentTypeLabel && styles.transparent)}
                />
                {contentTypeLabel}
              </div>
            )
          })}
        </div>
        {stripItems.length > 0 && (
          <GalleryStripSection
            items={stripItems}
            itemsLeft={itemsLeft}
            isShowMorePlaceholderVisible={isShowMorePlaceholderVisible}
            onMouseEnter={(index) => onMagnifyImage(index)}
            onSelectItemByIndex={(index) => onSelectItemHandler(index)}
            activeMagnifiedIndex={magnifiedItemIndex}
          />
        )}
      </div>
      {modalOpen && (
        <GalleryModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          items={items}
          selectedIdx={selectedItemIndex}
        />
      )}
    </>
  )
}
